import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Page,
} from "@syncfusion/ej2-react-grids";
import { AiOutlineSearch } from "react-icons/ai";

import EmptyData from "../../../components/EmptyData";
import SearchInputField from "../../Components/Forms/SearchInputField";
import SearchInputDocument from "../../Components/Forms/SearchInputDocument";
import { ToastComponent } from "../../Components/ToastComponent";
import { clientService } from "../../../Services/client.service";
import { paginationService } from "../../../Services/pagination.service";

const STATUS_OPTIONS = ["Ativo", "Inativo", "Suspenso", "Cancelado"];

const formatDate = (isoDate) => {
  if (!isoDate) return "";
  const [year, month, day] = isoDate.split("-");
  return `${day}/${month}/${year}`;
};

const formatDoc = (doc) => {
  const digits = (doc ?? "").toString().replace(/\D/g, "");
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  }
  if (digits.length === 14) {
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  }
  return doc ?? "";
};

// Aba "Ativos" do cliente (issue #504) — busca todos os contratos ativos
// vinculados ao cliente do usuário autenticado via GET
// /api/v3/customers/contracts/active. O endpoint não aceita filtro por
// query, então a lista inteira é carregada uma vez (no mount) e o form
// de pagante/status filtra em cima desses dados, no front.
export default function Assets() {
  const { t } = useTranslation();

  const [payerName, setPayerName] = useState("");
  const [payerDoc, setPayerDoc] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [allContracts, setAllContracts] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loadingContracts, setLoadingContracts] = useState(true);
  const [toastParams, setToastParams] = useState("");

  // Altura e paginação dinâmicos do grid (mesmo padrão de CustomerSearch.jsx)
  const [pageSize, setPageSize] = useState(15);
  const gridAnchorRef = useRef(null);

  const ROW_HEIGHT = 36;
  // gridAnchorRef já mede o "top" real do grid, então esse budget cobre só o
  // que fica ABAIXO dele — cabeçalho + pager do próprio grid Syncfusion.
  const HEADER_PAGER_BUDGET = 70;
  const BOTTOM_MARGIN = 134;
  const MIN_PAGE = 5;
  const MAX_PAGE = 100;

  const showToast = (title, body, color) => {
    setToastParams(JSON.stringify({ show: true, title, body, color }));
  };

  const calculateGridMetrics = () => {
    paginationService.calculateGridMetrics(
      gridAnchorRef,
      BOTTOM_MARGIN,
      HEADER_PAGER_BUDGET,
      ROW_HEIGHT,
      MIN_PAGE,
      MAX_PAGE,
      contracts,
      window.innerHeight,
      setPageSize
    );
  };

  useEffect(() => {
    if (!hasSearched || contracts.length === 0) return undefined;

    calculateGridMetrics();

    let resizeTimeout = null;
    const onResize = () => {
      if (resizeTimeout) window.clearTimeout(resizeTimeout);
      resizeTimeout = window.setTimeout(calculateGridMetrics, 50);
    };

    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      if (resizeTimeout) window.clearTimeout(resizeTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSearched, contracts]);

  const loadActiveContracts = useCallback(async () => {
    setLoadingContracts(true);
    const data = await clientService.fetchActiveContracts();

    if (data) {
      // Ajustar aqui se o backend vier num envelope (ex: data.items)
      // em vez de array puro — confirmar no Network tab.
      setAllContracts(Array.isArray(data) ? data : data.items ?? []);
    } else {
      setAllContracts([]);
      showToast(
        t("CUSTOMER.ASSETS.LOAD_ERROR_TITLE"),
        t("CUSTOMER.ASSETS.LOAD_ERROR_BODY"),
        "Danger"
      );
    }

    setLoadingContracts(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadActiveContracts();
  }, [loadActiveContracts]);

  const handleClear = () => {
    setPayerName("");
    setPayerDoc("");
    setStatusFilter("");
  };

  const handleSearchContracts = (e) => {
    e?.preventDefault?.();

    const docDigits = payerDoc.replace(/\D/g, "");

    const filtered = allContracts.filter((contract) => {
      const matchesDoc = docDigits ? contract.doc?.includes(docDigits) : true;
      const matchesStatus = statusFilter ? contract.status === statusFilter : true;
      return matchesDoc && matchesStatus;
    });

    setContracts(filtered);
    setHasSearched(true);
  };

  const handleShowDetails = (contract) => {
    showToast(
      t("CUSTOMER.ASSETS.DETAILS_TITLE"),
      t("CUSTOMER.ASSETS.DETAILS_WIP", { contractId: contract.contractId }),
      "Warning"
    );
  };

  return (
    <div className="p-3">
      <ToastComponent
        params={toastParams}
        closeToast={() => setToastParams(JSON.stringify({ show: false }))}
      />

      <p className="text-muted">{t("CUSTOMER.ASSETS.PAYER_HINT")}</p>

      <form onSubmit={handleSearchContracts}>
        <h6 className="fw-bold mb-2">{t("CUSTOMER.ASSETS.SEARCH_PAYER_TITLE")}</h6>
        <div className="row g-2 align-items-end mb-3">
          <div className="col-md-4">
            <SearchInputField
              name="payerName"
              label={t("COMMON.NAME")}
              type="text"
              value={payerName}
              onChange={(e) => setPayerName(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <SearchInputDocument
              name="payerDoc"
              label={t("COMMON.CPF_CNPJ")}
              value={payerDoc}
              onChange={(e) => setPayerDoc(e.target.value)}
            />
          </div>
          <div className="col-md-auto">
            <button type="submit" className="btn btn-primary" disabled={loadingContracts}>
              <AiOutlineSearch className="icon-in-button" />
              {t("COMMON.SEARCH")}
            </button>
          </div>
          <div className="col-md-auto">
            <button type="button" className="btn btn-outline-secondary" onClick={handleClear}>
              {t("CUSTOMER.ASSETS.CLEAR")}
            </button>
          </div>
        </div>

        <div className="row g-3 align-items-end mb-3">
          <div className="col-md-3">
            <label className="form-label">{t("CUSTOMER.ASSETS.STATUS_LABEL")}</label>
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">{t("CUSTOMER.ASSETS.SELECT_PLACEHOLDER")}</option>
              {STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">{t("CUSTOMER.ASSETS.TOTAL_CONTRACTS")}</label>
            <input
              type="text"
              className="form-control"
              value={loadingContracts ? "..." : allContracts.length}
              disabled
              readOnly
            />
          </div>
        </div>

        <button type="submit" className="btn btn-primary mb-4" disabled={loadingContracts}>
          {t("CUSTOMER.ASSETS.SEARCH_CONTRACTS")}
        </button>
      </form>

      <h6 className="fw-bold mb-3">{t("CUSTOMER.ASSETS.RESULTS_TITLE")}</h6>

      {loadingContracts ? (
        <p className="text-muted mb-0">{t("COMMON.LOADING")}</p>
      ) : !hasSearched ? (
        <p className="text-muted mb-0">{t("CUSTOMER.ASSETS.SEARCH_HINT")}</p>
      ) : contracts.length === 0 ? (
        <EmptyData />
      ) : (
        <>
          <div ref={gridAnchorRef} />
          <GridComponent
            dataSource={contracts}
            allowPaging
            pageSettings={{ pageSize }}
            rowHeight={ROW_HEIGHT}
            height="auto"
            actionComplete={calculateGridMetrics}
          >
            <ColumnsDirective>
              <ColumnDirective
                field="contractId"
                headerText={t("CUSTOMER.ASSETS.COLUMN_CONTRACT")}
                width="100"
              />
              <ColumnDirective field="plate" headerText={t("CUSTOMER.ASSETS.COLUMN_PLATE")} width="100" />
              <ColumnDirective field="serial" headerText={t("CUSTOMER.ASSETS.COLUMN_SERIAL")} width="150" />
              <ColumnDirective
                field="registeredAt"
                headerText={t("CUSTOMER.ASSETS.COLUMN_REGISTERED_AT")}
                width="120"
                template={(row) => <span>{formatDate(row.registeredAt)}</span>}
              />
              <ColumnDirective
                field="activatedAt"
                headerText={t("CUSTOMER.ASSETS.COLUMN_ACTIVATED_AT")}
                width="120"
                template={(row) => <span>{formatDate(row.activatedAt)}</span>}
              />
              <ColumnDirective
                field="lastChangeAt"
                headerText={t("CUSTOMER.ASSETS.COLUMN_LAST_CHANGE_AT")}
                width="130"
                template={(row) => <span>{formatDate(row.lastChangeAt)}</span>}
              />
              <ColumnDirective field="status" headerText={t("CUSTOMER.ASSETS.COLUMN_STATUS")} width="100" />
              <ColumnDirective
                field="doc"
                headerText={t("COMMON.CPF_CNPJ")}
                width="150"
                template={(row) => <span>{formatDoc(row.doc)}</span>}
              />
              <ColumnDirective
                field="commercialCondition"
                headerText={t("CUSTOMER.ASSETS.COLUMN_COMMERCIAL_CONDITION")}
                width="240"
              />
              <ColumnDirective
                headerText={t("CUSTOMER.ASSETS.COLUMN_ACTIONS")}
                width="140"
                textAlign="Center"
                template={(row) => (
                  <div className="py-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={() => handleShowDetails(row)}
                    >
                      {t("CUSTOMER.ASSETS.DETAILS_BUTTON")}
                    </button>
                  </div>
                )}
              />
            </ColumnsDirective>
            <Inject services={[Page]} />
          </GridComponent>
        </>
      )}
    </div>
  );
}
